/**
 * AR Exception Report - API Service
 *
 * HTTP client for AR reconciliation API endpoints
 */

import type {
  ArExceptionFlag,
  ArExceptionInvoice,
  ArPivotClient,
  ArExceptionResponse,
  ArPivotResponse,
  ArSummaryStats,
  ArHealthCheck,
  ReportClient,
} from '@repo/types';

// Local extensions for backward compatibility
interface ArClientBreakdownResponse {
  client: ArPivotClient;
  invoices: ArExceptionInvoice[];
}


// Request parameter types
interface ArFilters {
  clients?: string[];
  flags?: ArExceptionFlag[];
}

interface ArExceptionParams {
  clientIds?: string[]; // Client IDs for exceptions endpoint
  flags?: ArExceptionFlag[]; // Status flags for filtering
  page?: number;
  limit?: number;
}

interface ArPivotParams {
  clientIds?: string[]; // Client IDs for pivot endpoint
  page?: number;
  limit?: number;
}

interface ArExportParams extends ArFilters {
  type: 'csv' | 'xls' | 'pdf';
  includeMetadata?: boolean;
}

import { api } from '@xtnd/web/shared/api';

class ArApiService {
  private readonly baseResource = '/reports/ar';
  /**
   * Build query string from parameters
   */
  private buildQueryString(params: Record<string, unknown>): string {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(item => searchParams.append(`${key}[]`, String(item)));
        } else {
          searchParams.set(key, String(value));
        }
      }
    });

    return searchParams.toString();
  }

  /**
   * Get invoice exceptions with pagination
   */
  async getInvoiceExceptions(params: ArExceptionParams): Promise<ArExceptionResponse> {
    const queryString = this.buildQueryString(params as Record<string, unknown>);
    const endpoint = `${this.baseResource}/exceptions${queryString ? `?${queryString}` : ''}`;

    return await api.get<ArExceptionResponse>(endpoint);
  }

  /**
   * Get client pivot data
   */
  async getClientPivot(params: ArPivotParams): Promise<ArPivotResponse> {
    const queryString = this.buildQueryString(params as Record<string, unknown>);
    const endpoint = `${this.baseResource}/pivot${queryString ? `?${queryString}` : ''}`;

    return await api.get<ArPivotResponse>(endpoint);
  }

  /**
   * Get detailed client breakdown
   */
  async getClientBreakdown(
    clientName: string,
    options: {
      excludeCurrencyMismatch?: boolean;
      sortBy?: 'variance' | 'clioOpen' | 'qboOpen' | 'invoiceNumber';
      sortDirection?: 'asc' | 'desc';
    } = {}
  ): Promise<ArClientBreakdownResponse> {
    const encodedClientName = encodeURIComponent(clientName);
    const queryString = this.buildQueryString(options);
    const endpoint = `${this.baseResource}/client-breakdown/${encodedClientName}${queryString ? `?${queryString}` : ''}`;

    return await api.get<ArClientBreakdownResponse>(endpoint);
  }

  /**
   * Get summary statistics for all data (no filters)
   */
  async getSummaryStats(): Promise<ArSummaryStats> {
    const endpoint = '/reports/ar/summary';
    return await api.get<ArSummaryStats>(endpoint);
  }

  /**
   * Get service health check
   */
  async getHealthCheck(): Promise<ArHealthCheck> {
    const endpoint = '/reports/ar/health';
    return await api.get<ArHealthCheck>(endpoint);
  }

  /**
   * Export data in specified format
   */
  async exportData(params: ArExportParams): Promise<void> {
    // Use api.post to get the blob response
    const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8080'}/reports/ar/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error(`Export failed: ${response.status} ${response.statusText}`);
    }

    // Get filename from response headers or use default with timestamp
    const contentDisposition = response.headers.get('content-disposition');
    const now = new Date();
    const timeStamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
    let filename = `ar-exceptions-${timeStamp}.${params.type}`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    // Create blob and trigger download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    // Create temporary download link
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Get available clients for filtering
   */
  async getAvailableClients(): Promise<ReportClient[]> {
    const endpoint = `${this.baseResource}/clients`;
    return await api.get<ReportClient[]>(endpoint);
  }

  /**
   * Preflight check to ensure API is accessible
   */
  async checkConnection(): Promise<boolean> {
    try {
      await this.getHealthCheck();
      return true;
    } catch {
      return false;
    }
  }
}

// Export singleton instance
export const arReportApi = new ArApiService();

// Export types for use in components
export type {
  ArClientBreakdownResponse,
  ArFilters,
  ArExceptionParams,
  ArPivotParams,
  ArExportParams,
};

// Re-export shared types from @repo/types
export type {
  ArExceptionResponse,
  ArPivotResponse,
  ArSummaryStats,
  ArHealthCheck,
  ArExceptionFlag,
  ConfidenceLevel,
  ArExceptionInvoice,
  ArPivotClient,
  ReportClient,
} from '@repo/types';
